import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export interface CreatePaymentRequest {
  amount: {
    currency: string;
    value: string;
  };
  description: string;
  redirectUrl: string;
  metadata?: Record<string, any>;
  profileId?: string;
}

export interface MolliePayment {
  id: string;
  mode: string;
  createdAt: string;
  status: string;
  amount: {
    value: string;
    currency: string;
  };
  description: string;
  method: string | null;
  metadata: Record<string, any>;
  _links: {
    checkout?: {
      href: string;
    };
    self: {
      href: string;
    };
  };
}

export const SHOP_BETA = true;
export const BETA_DISCOUNT_PERCENTAGE = 25;
export const PUBLIC_KEY_DISCOUNT_PERCENTAGE = 10;

export interface PaymentType {
  type: 'premium_monthly' | 'premium_yearly' | 'level2_boost' | 'level3_boost' | 'level4_boost' | 'twitter_post' | 'newsletter_basic' | 'press_kit' | 'creator_push' | 'player_boost_3d' | 'player_boost_7d' | 'player_boost_30d' | 'player_border_beta' | 'player_border_gold' | 'player_border_neon';
  amount: number;
  description: string;
  metadata?: Record<string, any>;
}

const PAYMENT_TYPES: Record<string, PaymentType> = {
  premium_monthly: {
    type: 'premium_monthly',
    amount: 29.99,
    description: 'Premium Studio - Monthly Subscription',
  },
  premium_yearly: {
    type: 'premium_yearly',
    amount: 299.99,
    description: 'Premium Studio - Yearly Subscription',
  },
  level2_boost: {
    type: 'level2_boost',
    amount: 9.99,
    description: 'Level 2 Boost - 1 week',
  },
  level3_boost: {
    type: 'level3_boost',
    amount: 19.99,
    description: 'Level 3 Boost - 1 week',
  },
  level4_boost: {
    type: 'level4_boost',
    amount: 29.99,
    description: 'Level 4 Boost - 1 week',
  },
  twitter_post: {
    type: 'twitter_post',
    amount: 14.99,
    description: 'X/Twitter Post Promotion',
  },
  newsletter_basic: {
    type: 'newsletter_basic',
    amount: 19.99,
    description: 'Newsletter Basic Promotion',
  },
  press_kit: {
    type: 'press_kit',
    amount: 14.99,
    description: 'Press Kit Auto-Builder',
  },
  creator_push: {
    type: 'creator_push',
    amount: 19.99,
    description: 'Creator Push Promotion',
  },
  player_boost_3d: {
    type: 'player_boost_3d',
    amount: 3.99,
    description: 'Player Gems Boost - 3 Days',
  },
  player_boost_7d: {
    type: 'player_boost_7d',
    amount: 6.99,
    description: 'Player Gems Boost - 7 Days',
  },
  player_boost_30d: {
    type: 'player_boost_30d',
    amount: 24.99,
    description: 'Player Gems Boost - 30 Days',
  },
  player_border_beta: {
    type: 'player_border_beta',
    amount: 1.99,
    description: 'Beta Avatar Border',
  },
  player_border_gold: {
    type: 'player_border_gold',
    amount: 2.99,
    description: 'Golden Avatar Border',
  },
  player_border_neon: {
    type: 'player_border_neon',
    amount: 2.99,
    description: 'Neon Avatar Border',
  },
  player_border: {
    type: 'player_border' as any, // Generic type for dynamic borders
    amount: 0, // Will be fetched from database
    description: 'Custom Avatar Border', // Will be fetched from database
  },
};

/**
 * Create a payment via Mollie API (using Supabase Edge Function)
 */
export async function createMolliePayment(
  userId: string,
  paymentType: string,
  isBetaUser: boolean = false,
  metadata?: Record<string, any>,
  hasPublicKeyReduction: boolean = false
): Promise<{ success: boolean; paymentUrl?: string; paymentId?: string; error?: string }> {
  try {
    if (!SUPABASE_URL) {
      return { success: false, error: "Supabase URL not configured" };
    }

    let paymentConfig = PAYMENT_TYPES[paymentType];

    // If not a standard type, check if we have a dynamic item_id
    // We accept execution to proceed to the backend which will now validate against the DB
    if (!paymentConfig && !metadata?.item_id) {
      return { success: false, error: "Invalid payment type" };
    }

    // Apply discount if user is beta member or has public key reduction
    let finalAmount = paymentConfig?.amount || 0; // Default to 0, logic downstream handles it or backend handles dynamic price
    if (paymentConfig && (paymentType === 'premium_monthly' || paymentType === 'premium_yearly')) {
      let totalDiscount = 0;
      if (isBetaUser) totalDiscount += BETA_DISCOUNT_PERCENTAGE;
      if (hasPublicKeyReduction) totalDiscount += PUBLIC_KEY_DISCOUNT_PERCENTAGE;

      if (totalDiscount > 0) {
        finalAmount = paymentConfig.amount * (1 - totalDiscount / 100);
      }
    }


    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { success: false, error: "Not authenticated" };
    }

    // Call Supabase Edge Function
    const response = await fetch(`${SUPABASE_URL}/functions/v1/mollie-payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action: "create",
        paymentType,
        customAmount: finalAmount,
        metadata: {
          ...metadata,
          is_beta: SHOP_BETA,
          has_public_key_reduction: hasPublicKeyReduction
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Mollie payment function error:", errorData);
      return {
        success: false,
        error: errorData.error || `Error: ${response.statusText}`
      };
    }

    const result = await response.json();

    if (!result.success) {
      return {
        success: false,
        error: result.error || "Failed to create payment"
      };
    }

    // Store payment ID in localStorage for callback
    if (result.paymentId) {
      localStorage.setItem("mollie_payment_id", result.paymentId);
      localStorage.setItem("mollie_payment_data", JSON.stringify({
        userId,
        paymentType: paymentConfig?.type || paymentType,
        metadata,
      }));
    }

    return {
      success: true,
      paymentUrl: result.paymentUrl,
      paymentId: result.paymentId,
    };
  } catch (error) {
    console.error("Error creating Mollie payment:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
}

/**
 * Check payment status via Mollie API (using Supabase Edge Function)
 */
export async function checkPaymentStatus(
  paymentId: string
): Promise<{ success: boolean; status?: string; error?: string }> {
  try {
    if (!SUPABASE_URL) {
      return { success: false, error: "Supabase URL not configured" };
    }

    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { success: false, error: "Not authenticated" };
    }

    // Call Supabase Edge Function
    const response = await fetch(`${SUPABASE_URL}/functions/v1/mollie-payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action: "check",
        paymentId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `Error: ${response.statusText}`
      };
    }

    const result = await response.json();

    if (!result.success) {
      return {
        success: false,
        error: result.error || "Failed to check payment status"
      };
    }

    return {
      success: true,
      status: result.status,
    };
  } catch (error) {
    console.error("Error checking payment status:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
}

/**
 * Refund a payment via Mollie API (using Supabase Edge Function)
 */
export async function refundMolliePayment(
  molliePaymentId: string,
  amount?: number
): Promise<{ success: boolean; refundId?: string; error?: string }> {
  try {
    if (!SUPABASE_URL) {
      return { success: false, error: "Supabase URL not configured" };
    }

    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { success: false, error: "Not authenticated" };
    }

    // Call Supabase Edge Function
    const response = await fetch(`${SUPABASE_URL}/functions/v1/mollie-payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action: "refund",
        molliePaymentId,
        amount,
      }),
    });

    console.log("Refund API response status:", response.status);
    console.log("Refund API response headers:", Object.fromEntries(response.headers.entries()));

    let result: any;
    try {
      const text = await response.text();
      console.log("Refund API response text:", text);
      result = text ? JSON.parse(text) : {};
    } catch (parseError) {
      console.error("Error parsing refund response:", parseError);
      return {
        success: false,
        error: `Failed to parse response: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`
      };
    }

    if (!response.ok) {
      console.error("Refund API error response:", result);
      return {
        success: false,
        error: result.error || result.message || `Error: ${response.statusText}`
      };
    }

    if (!result.success) {
      console.error("Refund API returned success: false", result);
      return {
        success: false,
        error: result.error || result.message || "Failed to process refund"
      };
    }

    console.log("Refund successful:", result);
    return {
      success: true,
      refundId: result.refundId,
    };
  } catch (error) {
    console.error("Error refunding Mollie payment:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
}

/**
 * Update payment status in database and activate premium if needed
 */
export async function updatePaymentStatus(
  paymentId: string,
  status: string
): Promise<{ success: boolean; error?: string; paymentType?: string }> {
  try {
    const updateData: any = {
      status,
      updated_at: new Date().toISOString(),
    };

    if (status === "paid") {
      updateData.paid_at = new Date().toISOString();
    }

    const { data: payment, error: fetchError } = await supabase
      .from("payments")
      .select("user_id, payment_type, metadata, paid_at")
      .eq("mollie_payment_id", paymentId)
      .single();

    if (fetchError || !payment) {
      console.error("Payment fetch error:", fetchError);
      return { success: false, error: "Payment not found" };
    }

    console.log("Payment found:", {
      payment_type: payment.payment_type,
      metadata: payment.metadata,
      user_id: payment.user_id
    });

    // Update payment status
    const { error: updateError } = await supabase
      .from("payments")
      .update(updateData)
      .eq("mollie_payment_id", paymentId);

    if (updateError) {
      return { success: false, error: updateError.message };
    }

    // Process payment based on type
    if (status === "paid") {
      // Premium subscriptions
      if (payment.payment_type.startsWith("premium_")) {
        const { error: premiumError } = await supabase.rpc("activate_premium_after_payment", {
          p_user_id: payment.user_id,
          p_payment_type: payment.payment_type,
          p_amount: 0,
          p_is_beta: payment.metadata?.is_beta || false
        });

        if (premiumError) {
          console.error("Error activating premium:", premiumError);
        }
      }
      // Boosts (press kit, level boosts, etc.)
      else if (payment.payment_type !== "premium_monthly" && payment.payment_type !== "premium_yearly") {
        console.log("Processing boost payment:", payment.payment_type);

        // Process boost
        const { error: boostError } = await supabase.rpc("process_boost_after_payment", {
          p_user_id: payment.user_id,
          p_payment_type: payment.payment_type,
          p_metadata: payment.metadata || {},
          p_paid_at: payment.paid_at || null,
        });

        if (boostError) {
          console.error("Error processing boost:", boostError);
        } else {
          console.log("Boost processed successfully");
        }

        // If it's a press kit, generate it
        if (payment.payment_type === "press_kit") {
          console.log("Press kit payment detected, checking metadata...");
          console.log("Metadata type:", typeof payment.metadata);
          console.log("Metadata:", JSON.stringify(payment.metadata, null, 2));

          // Handle both object and JSONB formats
          let metadataObj: any = payment.metadata;
          if (typeof metadataObj === 'string') {
            try {
              metadataObj = JSON.parse(metadataObj);
            } catch (e) {
              console.error("Failed to parse metadata string:", e);
            }
          }

          const gameId = metadataObj?.game_id;

          console.log("Extracted game_id:", gameId);

          if (!gameId) {
            console.error("❌ No game_id found in payment metadata");
            console.error("Available metadata keys:", metadataObj ? Object.keys(metadataObj) : "none");
            return {
              success: false,
              error: "game_id is required in payment metadata for press kit generation",
              paymentType: payment.payment_type
            };
          }

          console.log("✅ Game ID found:", gameId, "Generating press kit for user:", payment.user_id);

          try {
            // Import dynamically to avoid circular dependencies
            const { generatePressKit } = await import("./pressKitService");
            console.log("Calling generatePressKit...");
            const pressKitResult = await generatePressKit(
              gameId,
              payment.user_id
            );

            console.log("Press kit generation result:", pressKitResult);

            if (pressKitResult.success) {
              console.log("✅ Press kit generated successfully!");
            } else {
              console.error("❌ Error generating press kit:", pressKitResult.error);
              // Return error so it can be displayed to user
              return {
                success: false,
                error: `Failed to generate press kit: ${pressKitResult.error}`,
                paymentType: payment.payment_type
              };
            }
          } catch (error) {
            console.error("❌ Exception while generating press kit:", error);
            console.error("Error stack:", error instanceof Error ? error.stack : "No stack");
            return {
              success: false,
              error: error instanceof Error ? error.message : "Unknown error generating press kit",
              paymentType: payment.payment_type
            };
          }
        }
      }
    }

    return { success: true, paymentType: payment.payment_type };
  } catch (error) {
    console.error("Error updating payment status:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
}

export { PAYMENT_TYPES };

